
import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Plus, RefreshCw, FileText, Bell, Send, ChevronDown, MessageSquare } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'

interface QuickActionsProps {
  onRefresh: () => void
  refreshing?: boolean
}

export function QuickActions({ onRefresh, refreshing = false }: QuickActionsProps) {
  const { toast } = useToast()
  const [sendingSlackReport, setSendingSlackReport] = useState(false)
  const [creatingChannels, setCreatingChannels] = useState(false)
  const [testingChannel, setTestingChannel] = useState(false)

  const handleAction = (action: string) => {
    toast({
      title: "Feature Coming Soon",
      description: `${action} functionality will be available in the next update.`,
    })
  }

  const handleSendSlackReport = async (reportType: 'daily' | 'weekly' | 'monthly' = 'daily') => {
    setSendingSlackReport(true)
    try {
      const { data, error } = await supabase.functions.invoke('daily-slack-report', {
        body: { reportType }
      })
      
      if (error) throw error

      const reportTypeLabel = reportType.charAt(0).toUpperCase() + reportType.slice(1);
      toast({
        title: "Slack Report Sent",
        description: `${reportTypeLabel} outbound report has been sent to Slack successfully.`,
      })
    } catch (error) {
      console.error('Error sending Slack report:', error)
      toast({
        title: "Error",
        description: "Failed to send Slack report. Please try again.",
        variant: "destructive",
      })
    } finally {
      setSendingSlackReport(false)
    }
  }

  const handleSendMailboxHealth = async () => {
    setSendingSlackReport(true)
    try {
      const { data, error } = await supabase.functions.invoke('daily-mailbox-health-slack')
      
      if (error) throw error

      toast({
        title: "Mailbox Health Report Sent",
        description: "Mailbox health report has been sent to Slack successfully.",
      })
    } catch (error) {
      console.error('Error sending mailbox health report:', error)
      toast({
        title: "Error",
        description: "Failed to send mailbox health report. Please try again.",
        variant: "destructive",
      })
    } finally {
      setSendingSlackReport(false)
    }
  }

  const handleSendDomainRenewalReport = async () => {
    setSendingSlackReport(true)
    try {
      const { data, error } = await supabase.functions.invoke('weekly-domain-renewal-report')
      
      if (error) throw error

      toast({
        title: "Domain Renewal Report Sent",
        description: "Weekly domain renewal report has been sent to Slack successfully.",
      })
    } catch (error) {
      console.error('Error sending domain renewal report:', error)
      toast({
        title: "Error",
        description: "Failed to send domain renewal report. Please try again.",
        variant: "destructive",
      })
    } finally {
      setSendingSlackReport(false)
    }
  }

  const handleSendDomainStatusReport = async () => {
    setSendingSlackReport(true)
    try {
      const { data, error } = await supabase.functions.invoke('weekly-domain-status-report')
      
      if (error) throw error

      toast({
        title: "Domain Status Report Sent",
        description: "Weekly domain status report has been sent to Slack successfully.",
      })
    } catch (error) {
      console.error('Error sending domain status report:', error)
      toast({
        title: "Error",
        description: "Failed to send domain status report. Please try again.",
        variant: "destructive",
      })
    } finally {
      setSendingSlackReport(false)
    }
  }

  const handleSendOrphanedMailboxesAlert = async () => {
    setSendingSlackReport(true)
    try {
      const { data, error } = await supabase.functions.invoke('smartlead-orphaned-mailboxes-alert')
      
      if (error) throw error

      toast({
        title: "Orphaned Mailboxes Alert Sent",
        description: "Smartlead orphaned mailboxes alert has been sent to Slack successfully.",
      })
    } catch (error) {
      console.error('Error sending orphaned mailboxes alert:', error)
      toast({
        title: "Error",
        description: "Failed to send orphaned mailboxes alert. Please try again.",
        variant: "destructive",
      })
    } finally {
      setSendingSlackReport(false)
    }
  }

  const handleDailyMailboxSync = async () => {
    setSendingSlackReport(true)
    try {
      const { data, error } = await supabase.functions.invoke('daily-mailbox-status-sync')
      
      if (error) throw error

      toast({
        title: "Daily Mailbox Sync Started",
        description: "Daily mailbox status sync has been initiated. Check Slack for results.",
      })
    } catch (error) {
      console.error('Error starting daily mailbox sync:', error)
      toast({
        title: "Error",
        description: "Failed to start daily mailbox sync. Please try again.",
        variant: "destructive",
      })
    } finally {
      setSendingSlackReport(false)
    }
  }

  const handleSendMissingDomainsReport = async () => {
    setSendingSlackReport(true)
    try {
      const { data, error } = await supabase.functions.invoke('weekly-missing-domains-report')
      
      if (error) throw error

      toast({
        title: "Missing Domains Report Sent",
        description: "Weekly missing domains report has been sent to Slack successfully.",
      })
    } catch (error) {
      console.error('Error sending missing domains report:', error)
      toast({
        title: "Error",
        description: "Failed to send missing domains report. Please try again.",
        variant: "destructive",
      })
    } finally {
      setSendingSlackReport(false)
    }
  }

  const handleCreateTestChannels = async () => {
    setCreatingChannels(true)
    try {
      const { data, error } = await supabase.functions.invoke('create-test-channels')
      
      if (error) throw error

      toast({
        title: "Test Channels Created",
        description: "All 5 test channels for AI reply alerts have been created successfully.",
      })
    } catch (error) {
      console.error('Error creating test channels:', error)
      toast({
        title: "Error",
        description: "Failed to create test channels. Please try again.",
        variant: "destructive",
      })
    } finally {
      setCreatingChannels(false)
    }
  }

  const handleTestPositiveReplyAlert = async (channel: string) => {
    setTestingChannel(true)
    try {
      // Sample test data
      const testData = {
        leadName: "John Smith",
        leadEmail: "john.smith@example.com",
        replyMessage: "Thanks for reaching out! I'm definitely interested in learning more about your solution.",
        appUrl: "https://example-app.com",
        companySiteTraffic: "10K monthly visitors",
        campaignName: "Q4 Enterprise Outreach",
        subject: "Re: Improving your sales process",
        platform: "LinkedIn",
        companyDomain: "example.com",
        leadJobTitle: "VP of Sales",
        leadLinkedinUrl: "https://linkedin.com/in/johnsmith",
        category: "Enterprise Lead",
        channel: channel
      }
      
      const { data, error } = await supabase.functions.invoke('send-positive-reply-alert', {
        body: testData
      })
      
      if (error) throw error

      toast({
        title: "Test Alert Sent",
        description: `Positive reply alert has been sent to ${channel} successfully.`,
      })
    } catch (error) {
      console.error('Error sending test alert:', error)
      toast({
        title: "Error",
        description: "Failed to send test alert. Please try again.",
        variant: "destructive",
      })
    } finally {
      setTestingChannel(false)
    }
  }

  return (
    <Card className="bg-white border border-gray-200 shadow-sm">
      <CardHeader>
        <CardTitle className="font-retro-star text-lg text-black-pearl">
          Quick Actions
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <Button 
            variant="outline" 
            className="flex-col h-auto py-4 px-3 hover:bg-blue-koi/10 hover:border-blue-koi"
            onClick={() => handleAction("Add New Campaign")}
          >
            <Plus className="w-5 h-5 mb-2 text-blue-koi" />
            <span className="text-sm font-medium">New Campaign</span>
          </Button>
          
          <Button 
            variant="outline" 
            className="flex-col h-auto py-4 px-3 hover:bg-greenish-cyan/10 hover:border-greenish-cyan"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-5 h-5 mb-2 text-greenish-cyan ${refreshing ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium">Sync Data</span>
          </Button>
          
          <Button 
            variant="outline" 
            className="flex-col h-auto py-4 px-3 hover:bg-reddish-orange/10 hover:border-reddish-orange"
            onClick={() => handleAction("Generate Report")}
          >
            <FileText className="w-5 h-5 mb-2 text-reddish-orange" />
            <span className="text-sm font-medium">Generate Report</span>
          </Button>
          
          <Button 
            variant="outline" 
            className="flex-col h-auto py-4 px-3 hover:bg-gray-500/10 hover:border-gray-500"
            onClick={() => handleAction("View All Alerts")}
          >
            <Bell className="w-5 h-5 mb-2 text-gray-600" />
            <span className="text-sm font-medium">View Alerts</span>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="flex-col h-auto py-4 px-3 hover:bg-purple-500/10 hover:border-purple-500"
                disabled={sendingSlackReport}
              >
                <div className="flex items-center mb-2">
                  <Send className={`w-5 h-5 text-purple-500 ${sendingSlackReport ? 'animate-pulse' : ''}`} />
                  <ChevronDown className="w-3 h-3 ml-1 text-purple-500" />
                </div>
                <span className="text-sm font-medium">Slack Reports</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-48">
              <DropdownMenuItem onClick={() => handleSendSlackReport('daily')}>
                📊 Daily Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSendSlackReport('weekly')}>
                📈 Weekly Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSendSlackReport('monthly')}>
                📆 Monthly Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSendMailboxHealth}>
                🔋 Mailbox Health
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSendDomainRenewalReport}>
                🔄 Domain Renewals
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSendDomainStatusReport}>
                📋 Domain Status
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSendMissingDomainsReport}>
                🚨 Missing Domains
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSendOrphanedMailboxesAlert}>
                🔍 Orphaned Mailboxes
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="flex-col h-auto py-4 px-3 hover:bg-yellow-500/10 hover:border-yellow-500"
                disabled={creatingChannels || testingChannel}
              >
                <div className="flex items-center mb-2">
                  <MessageSquare className={`w-5 h-5 text-yellow-500 ${creatingChannels || testingChannel ? 'animate-pulse' : ''}`} />
                  <ChevronDown className="w-3 h-3 ml-1 text-yellow-500" />
                </div>
                <span className="text-sm font-medium">Test Channels</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-56">
              <DropdownMenuItem onClick={handleCreateTestChannels}>
                🏗️ Create All Test Channels
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleTestPositiveReplyAlert('#ai-reply-tagger-yotpo-uk')}>
                🇬🇧 Test Yotpo UK
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleTestPositiveReplyAlert('#ai-reply-tagger-yotpo-us')}>
                🇺🇸 Test Yotpo US
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleTestPositiveReplyAlert('#ai-reply-tagger-yotpo-fr')}>
                🇫🇷 Test Yotpo FR
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleTestPositiveReplyAlert('#ai-reply-tagger-yotpo-au')}>
                🇦🇺 Test Yotpo AU
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleTestPositiveReplyAlert('#ai-reply-tagger-loop')}>
                🔄 Test Loop Replies
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  )
}
